import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  BarChart3, BookOpen, Brain, Check, CheckSquare, ChevronRight, Clock, Crown, Feather,
  FileText, Fingerprint, Lightbulb, Loader2, LogOut, Plus, RotateCcw, Share2, Shield,
  Sparkles, Square, Star, Trash2, X, Zap, MoreVertical, HelpCircle, PenTool, Gift,
  MessageCircle, Heart
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { playTick, playComplete, initAudio } from "../sounds";
import ShweebumCat from "../components/ShweebumCat";
import { WelcomePopup } from "../components/WelcomePopup";
import { GuidedTour } from "../components/GuidedTour";
import NewProjectDialog from "../components/NewProjectDialog";
import ShareRewardsModal from "../components/ShareRewardsModal";
import ContactSupportModal from "../components/writing/ContactSupportModal";
import { useAuth } from "../components/Auth/AuthProvider";
import { API, isWithinLoginGrace } from "../lib/api";

const Dashboard = () => {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewProject, setShowNewProject] = useState(false);
  const [showProjectTypeChooser, setShowProjectTypeChooser] = useState(false);
  const [showNewCookbook, setShowNewCookbook] = useState(false);
  const [creatingCookbook, setCreatingCookbook] = useState(false);
  const [newCookbookData, setNewCookbookData] = useState({ title: "", style: "single_author", cuisine: "" });
  const [showNewLifeStory, setShowNewLifeStory] = useState(false);
  const [creatingLifeStory, setCreatingLifeStory] = useState(false);
  const [newLifeStoryData, setNewLifeStoryData] = useState({ title: "", mode: "memoir", subject_name: "" });
  const FALLBACK_GENRES = [
    {id:"fantasy",name:"Fantasy"},{id:"romance",name:"Romance"},{id:"mystery",name:"Mystery"},
    {id:"thriller",name:"Thriller"},{id:"scifi",name:"Science Fiction"},{id:"horror",name:"Horror"},
    {id:"literary",name:"Literary Fiction"},{id:"historical",name:"Historical Fiction"},
    {id:"adventure",name:"Adventure"},{id:"drama",name:"Drama"},{id:"comedy",name:"Comedy"},
    {id:"dystopian",name:"Dystopian"},{id:"crime",name:"Crime"},{id:"urban",name:"Urban Fiction"},
    {id:"ya",name:"Young Adult"},{id:"paranormal",name:"Paranormal"},{id:"western",name:"Western"},
    {id:"spiritual",name:"Spiritual"},{id:"action",name:"Action"},{id:"dark_fiction",name:"Dark Fiction"},
  ];
  const [genres, setGenres] = useState(FALLBACK_GENRES);
  const [subscription, setSubscription] = useState(null);
  
  // Style profile state
  const [showStyleDialog, setShowStyleDialog] = useState(false);
  const [styleProfile, setStyleProfile] = useState(null);
  const [styleSample, setStyleSample] = useState("");
  const [analyzingStyle, setAnalyzingStyle] = useState(false);
  
  // Onboarding state
  const [showWelcome, setShowWelcome] = useState(false);
  const [showTour, setShowTour] = useState(false);
  
  // Share rewards state
  const [showShareRewards, setShowShareRewards] = useState(false);
  
  // Contact Support state
  const [showContactSupport, setShowContactSupport] = useState(false);
  
  // Cancel subscription state
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  
  // Word usage state
  const [wordUsage, setWordUsage] = useState(null);
  
  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Bulk selection state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedBooks, setSelectedBooks] = useState(new Set());

  // Track current user to detect actual user changes
  const prevUserIdRef = useRef(user?.id);

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }
    
    // Redirect free users to Freebum page (unless they're family/premium/plus)
    // Allow them to explicitly visit /dashboard if they want to see the old view
    const userTier = user.subscription?.toLowerCase() || 'free';
    const paidTiers = ['storyteller', 'plus', 'pro', 'author', 'premium', 'family'];
    const isPaidUser = paidTiers.includes(userTier);
    
    // Check if user explicitly navigated here (via "View Full Dashboard" link)
    const explicitDashboardVisit = sessionStorage.getItem('shweebum_explicit_dashboard');
    
    if (!isPaidUser && !explicitDashboardVisit) {
      // Free user - redirect to Freebum page
      navigate("/freebum", { replace: true });
      return;
    }
    
    // Clear the explicit visit flag after using it
    if (explicitDashboardVisit) {
      sessionStorage.removeItem('shweebum_explicit_dashboard');
    }
    
    // Only clear state if the user actually CHANGED (different user logged in)
    // This prevents layout jumps on normal page loads
    const userChanged = prevUserIdRef.current && prevUserIdRef.current !== user.id;
    if (userChanged) {
      setProjects([]);
      setLoading(true);
      setStyleProfile(null);
      setWordUsage(null);
      setSubscription(null);
    }
    prevUserIdRef.current = user.id;
    
    fetchProjects();
    fetchGenres();
    fetchSubscription();
    fetchStyleProfile();
    fetchWordUsage();
    
    // Initialize audio on first load (needed for browsers)
    initAudio();
    
    // Check for new user to show welcome popup
    const hasSeenWelcome = localStorage.getItem(`shweebum_welcomed_${user.id}`);
    if (!hasSeenWelcome) {
      setShowWelcome(true);
    }
    
    // Check for successful payment return
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const success = params.get('success');
    if (sessionId && success === 'true') {
      checkPaymentStatus(sessionId);
    }
  }, [user, navigate]);

  const fetchStyleProfile = async () => {
    try {
      const res = await axios.get(`${API}/style-profile/${user.id}`);
      if (res.data.has_profile) {
        setStyleProfile(res.data.style_profile);
      }
    } catch (e) {
      console.log("No style profile yet");
    }
  };
  
  // Handle welcome popup close and start tour
  const handleWelcomeClose = () => {
    setShowWelcome(false);
    localStorage.setItem(`shweebum_welcomed_${user.id}`, 'true');
    // Auto-start the guided tour after welcome popup
    setTimeout(() => setShowTour(true), 300);
  };
  
  // Handle tour completion
  const handleTourComplete = () => {
    setShowTour(false);
    localStorage.setItem(`shweebum_toured_${user.id}`, 'true');
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        toast.success("Payment successful! Your plan has been upgraded!");
        fetchSubscription();
        // Clear URL params
        window.history.replaceState({}, '', '/dashboard');
      }
    } catch (error) {
      console.error("Error checking payment:", error);
    }
  };

  const fetchSubscription = async () => {
    try {
      const res = await axios.get(`${API}/user/${user.id}/subscription`);
      setSubscription(res.data);
      updateUser({ subscription: res.data.subscription });
    } catch (error) {
      console.error("Error fetching subscription:", error);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const res = await axios.post(`${API}/subscription/cancel?user_id=${user.id}`);
      toast.success(res.data.message || "Cancellation scheduled!");
      setShowCancelDialog(false);
      fetchSubscription();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to cancel subscription");
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      const res = await axios.post(`${API}/subscription/reactivate?user_id=${user.id}`);
      toast.success(res.data.message || "Subscription reactivated!");
      fetchSubscription();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to reactivate");
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/projects/${user.id}`);
      if (user?.id) {
        setProjects(res.data);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      if (error?.response?.status === 403 || error?.response?.status === 401) {
        // Token/user mismatch or expired. NEVER instant-logout right after sign-in:
        // transient 401/403s during deploy rollouts caused a sign-in loop.
        // Within the grace window, retry once after a short delay instead.
        if (isWithinLoginGrace()) {
          console.warn('[Auth] 401/403 on projects within login grace — retrying instead of logout');
          setTimeout(() => { fetchProjects(); }, 4000);
          return;
        }
        logout();
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchGenres = async () => {
    try {
      const res = await axios.get(`${API}/genres`);
      setGenres(res.data.genres);
    } catch (error) {
      console.error("Error fetching genres:", error);
    }
  };

  const fetchWordUsage = async () => {
    try {
      const res = await axios.get(`${API}/usage/${user.id}`);
      setWordUsage(res.data);
    } catch (error) {
      console.error("Error fetching word usage:", error);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm("Are you sure you want to delete this project?")) return;
    try {
      await axios.delete(`${API}/project/${projectId}`);
      setProjects(projects.filter(p => p.id !== projectId));
      toast.success("Project deleted");
    } catch (error) {
      toast.error("Failed to delete project");
    }
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedBooks.size === 0) return;
    
    const bookCount = selectedBooks.size;
    if (!window.confirm(`Delete ${bookCount} book${bookCount > 1 ? 's' : ''}? This cannot be undone.`)) return;
    
    try {
      // Delete all selected books
      const deletePromises = Array.from(selectedBooks).map(id => 
        axios.delete(`${API}/project/${id}`)
      );
      await Promise.all(deletePromises);
      
      // Update state
      setProjects(projects.filter(p => !selectedBooks.has(p.id)));
      setSelectedBooks(new Set());
      setSelectionMode(false);
      toast.success(`Deleted ${bookCount} book${bookCount > 1 ? 's' : ''}`);
    } catch (error) {
      toast.error("Failed to delete some books");
    }
  };

  // Toggle book selection
  const toggleBookSelection = (projectId, e) => {
    e.stopPropagation();
    setSelectedBooks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  };

  // Select all books
  const selectAllBooks = () => {
    if (selectedBooks.size === projects.length) {
      setSelectedBooks(new Set());
    } else {
      setSelectedBooks(new Set(projects.map(p => p.id)));
    }
  };

  if (!user) return null;

  const isFreePlan = subscription?.subscription === 'free' || !subscription;
  const generationsRemaining = subscription?.generations_remaining ?? 3;

  return (
    <div className="min-h-screen bg-[#060A12]">
      {/* Header - Mobile Optimized - Destiny Dark Theme */}
      <header className="bg-[#0A0E17]/90 backdrop-blur-md border-b border-cyan-500/20 sticky top-0 z-40">
        <div className="w-full px-4 py-3">
          {/* Top row: Logo + Menu button on mobile, Full nav on desktop */}
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-3 group" data-testid="dashboard-logo">
              {/* Magical Book Icon */}
              <div className="relative">
                <div className="w-9 h-9 sm:w-11 sm:h-11 bg-gradient-to-br from-cyan-400/20 via-cyan-400/10 to-violet-500/20 rounded-lg flex items-center justify-center border border-cyan-500/20 shadow-lg shadow-cyan-500/20 group-hover:shadow-cyan-500/40 transition-all">
                  <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" />
                  <Sparkles className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-cyan-400 absolute -top-0.5 -right-0.5 animate-pulse" />
                </div>
              </div>
              {/* Brand Name */}
              <div className="flex flex-col">
                <span className="font-literary text-lg sm:text-2xl bg-gradient-to-r from-cyan-300 via-cyan-200 to-cyan-300 bg-clip-text text-transparent font-semibold tracking-wide">
                  Shweebum
                </span>
                <span className="text-[8px] sm:text-[10px] text-slate-500 -mt-0.5 tracking-widest uppercase">Your Creative Partner</span>
              </div>
            </Link>
            
            {/* Desktop nav items */}
            <div className="hidden md:flex items-center gap-3">
              {isFreePlan && (
                <Link to="/pricing">
                  <Button size="sm" className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 shadow-lg shadow-cyan-500/25 glow-gold" data-testid="upgrade-btn">
                    <Zap className="w-4 h-4 mr-2" />
                    Upgrade
                  </Button>
                </Link>
              )}
              {user.email?.toLowerCase() === "boldenbrett@gmail.com" && (
                <Link to="/admin/dashboard">
                  <Button size="sm" variant="ghost" className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10">
                    <Crown className="w-4 h-4 mr-1" />
                    Admin
                  </Button>
                </Link>
              )}
              <Button 
                size="sm" 
                variant="ghost" 
                className={`btn-pop ${styleProfile ? 'text-violet-400 glow-void' : 'text-slate-400 hover:text-violet-400'}`}
                onClick={() => {
                  const canUseContinueStyle = subscription?.limits?.can_continue_style || 
                                              subscription?.subscription === 'author' || 
                                              subscription?.subscription === 'family';
                  if (!canUseContinueStyle) {
                    toast.error("Continue My Style requires Shweebum Premium ($20/mo). Upgrade to unlock!");
                    return;
                  }
                  setShowStyleDialog(true);
                }}
                data-testid="my-style-btn"
              >
                <Fingerprint className="w-4 h-4 mr-1" />
                {styleProfile ? 'My Style ✓' : 'My Style'}
              </Button>
              
              {/* Tier badges - Destiny style glowing */}
              {user.subscription === 'family' && (
                <div className="flex items-center gap-1 bg-cyan-500/15 px-3 py-1.5 rounded-full border border-cyan-500/40 glow-cyan">
                  <Shield className="w-4 h-4 text-cyan-400" />
                  <span className="text-xs text-cyan-300 font-semibold">Family</span>
                </div>
              )}
              {(user.subscription === 'pro' || user.subscription === 'storyteller') && (
                <div className="flex items-center gap-1 bg-cyan-500/15 px-3 py-1.5 rounded-full border border-cyan-500/30 glow-gold">
                  <Star className="w-4 h-4 text-cyan-400" />
                  <span className="text-xs text-cyan-300 font-semibold">Shweebum Plus</span>
                </div>
              )}
              {user.subscription === 'author' && (
                <div className="flex items-center gap-1 bg-violet-500/15 px-3 py-1.5 rounded-full border border-violet-500/40 glow-void">
                  <Crown className="w-4 h-4 text-violet-400" />
                  <span className="text-xs text-violet-300 font-semibold">Shweebum Premium</span>
                </div>
              )}
              {user.subscription === 'promo' && (
                <div className="flex items-center gap-1 bg-amber-500/15 px-3 py-1.5 rounded-full border border-amber-500/40 animate-pulse" data-testid="promo-badge">
                  <Sparkles className="w-4 h-4 text-amber-400" />
                  <span className="text-xs text-amber-300 font-semibold">Shweebum Promo</span>
                </div>
              )}
              <span className="text-slate-400 text-sm">Hello, {user.name}</span>
              
              {/* Settings Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white hover:bg-slate-700/50" data-testid="settings-menu-btn">
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-slate-900 border-slate-700">
                  <DropdownMenuLabel className="text-slate-400">Options</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-slate-700" />
                  
                  {/* Free Features */}
                  <Link to="/scratchpad">
                    <DropdownMenuItem className="cursor-pointer text-emerald-400 focus:bg-emerald-500/10 focus:text-emerald-300">
                      <PenTool className="w-4 h-4 mr-2" />
                      Free Writing Pad
                      <Badge variant="outline" className="ml-auto text-[10px] border-emerald-500/50 text-emerald-400">FREE</Badge>
                    </DropdownMenuItem>
                  </Link>
                  
                  <Link to="/freebum">
                    <DropdownMenuItem className="cursor-pointer text-cyan-400 focus:bg-cyan-500/10 focus:text-cyan-300">
                      <Feather className="w-4 h-4 mr-2" />
                      View Freebum Page
                    </DropdownMenuItem>
                  </Link>
                  
                  <DropdownMenuItem 
                    className="cursor-pointer text-slate-300 focus:bg-slate-700 focus:text-white"
                    onClick={() => {
                      // Clear tour flag and show tour
                      if (user?.id) {
                        localStorage.removeItem(`shweebum_toured_${user.id}`);
                      }
                      setShowTour(true);
                    }}
                    data-testid="replay-tutorial-btn"
                  >
                    <HelpCircle className="w-4 h-4 mr-2" />
                    Replay Tutorial
                  </DropdownMenuItem>
                  
                  <DropdownMenuItem 
                    className="cursor-pointer text-emerald-400 focus:bg-emerald-500/10 focus:text-emerald-300"
                    onClick={() => setShowContactSupport(true)}
                    data-testid="contact-support-btn"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Contact Support
                  </DropdownMenuItem>
                  
                  <Link to="/pricing">
                    <DropdownMenuItem className="cursor-pointer text-cyan-400 focus:bg-cyan-500/10 focus:text-cyan-300">
                      <Star className="w-4 h-4 mr-2" />
                      View Plans & Pricing
                    </DropdownMenuItem>
                  </Link>
                  
                  <DropdownMenuSeparator className="bg-slate-700" />
                  
                  <DropdownMenuItem 
                    className="cursor-pointer text-red-400 focus:bg-red-500/10 focus:text-red-300"
                    onClick={() => {
                      localStorage.removeItem('shweebum_user');
                      localStorage.removeItem('shweebum_token');
                      sessionStorage.clear();
                      window.location.replace('/');
                    }}
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              <button
                type="button"
                onClick={() => {
                  localStorage.removeItem('shweebum_user');
                  localStorage.removeItem('shweebum_token');
                  sessionStorage.clear();
                  window.location.replace('/');
                }}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-colors text-slate-400 hover:text-white hover:bg-slate-700/50 h-8 rounded-md px-3 text-xs cursor-pointer"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
            
            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 rounded-lg text-slate-300 hover:bg-slate-700/50"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
          
          {/* Mobile dropdown menu - Destiny Dark Theme */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-3 pt-3 border-t border-cyan-500/20 space-y-2">
              {/* User info */}
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-white">Hello, {user.name}</span>
                {user.subscription === 'author' && (
                  <span className="text-xs bg-violet-500/20 text-violet-300 px-2 py-0.5 rounded-full border border-violet-500/30">Shweebum Premium</span>
                )}
                {(user.subscription === 'pro' || user.subscription === 'storyteller') && (
                  <span className="text-xs bg-cyan-500/20 text-cyan-300 px-2 py-0.5 rounded-full border border-cyan-500/20">Shweebum Plus</span>
                )}
              </div>
              
              {/* Action buttons */}
              <div className="grid grid-cols-2 gap-2">
                {isFreePlan && (
                  <Link to="/pricing" className="col-span-2">
                    <Button size="sm" className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 shadow-lg shadow-cyan-500/25">
                      <Zap className="w-4 h-4 mr-2" />
                      Upgrade Plan
                    </Button>
                  </Link>
                )}
                {user.email?.toLowerCase() === "boldenbrett@gmail.com" && (
                  <Link to="/admin/dashboard">
                    <Button size="sm" variant="outline" className="w-full text-cyan-400 border-cyan-500/30 hover:bg-cyan-500/10">
                      <Crown className="w-4 h-4 mr-2" />
                      Admin
                    </Button>
                  </Link>
                )}
                <Button 
                  size="sm" 
                  variant="outline"
                  className={`w-full ${styleProfile ? 'text-violet-400 border-violet-500/40' : 'text-slate-300 border-slate-600'}`}
                  onClick={() => {
                    const canUseContinueStyle = subscription?.limits?.can_continue_style || 
                                                subscription?.subscription === 'author' || 
                                                subscription?.subscription === 'family';
                    if (!canUseContinueStyle) {
                      toast.error("Continue My Style requires Shweebum Premium");
                      return;
                    }
                    setShowStyleDialog(true);
                    setMobileMenuOpen(false);
                  }}
                >
                  <Fingerprint className="w-4 h-4 mr-2" />
                  My Style
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full text-cyan-400 border-cyan-500/40 hover:bg-cyan-500/10"
                  onClick={() => {
                    if (user?.id) {
                      localStorage.removeItem(`shweebum_toured_${user.id}`);
                    }
                    setShowTour(true);
                    setMobileMenuOpen(false);
                  }}
                  data-testid="mobile-replay-tutorial-btn"
                >
                  <HelpCircle className="w-4 h-4 mr-2" />
                  Tutorial
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/10"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    window.location.href = '/freebum';
                  }}
                  data-testid="mobile-freebum-btn"
                >
                  <Feather className="w-4 h-4 mr-2" />
                  Freebum
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full text-teal-400 border-teal-500/40 hover:bg-teal-500/10"
                  onClick={() => {
                    setShowContactSupport(true);
                    setMobileMenuOpen(false);
                  }}
                  data-testid="mobile-contact-support-btn"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Contact Support
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full text-slate-300 border-slate-600 hover:bg-slate-700/50"
                  onClick={() => {
                    localStorage.removeItem('shweebum_user');
                    localStorage.removeItem('shweebum_token');
                    sessionStorage.clear();
                    window.location.replace('/');
                  }}
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Subscription Banner - Destiny Neon Theme */}
      {subscription && (
        <div className={`border-b ${
          subscription.subscription === 'free' ? 'bg-cyan-950/30 border-cyan-500/20' :
          subscription.subscription === 'storyteller' || subscription.subscription === 'pro' ? 'bg-slate-800/40 border-cyan-500/15' :
          subscription.subscription === 'author' ? 'bg-violet-950/30 border-violet-500/20' :
          subscription.subscription === 'promo' ? 'bg-amber-950/30 border-amber-500/20' :
          'bg-emerald-950/30 border-emerald-500/20'
        }`}>
          <div className="w-full px-4 py-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full shrink-0 ${
                  subscription.subscription === 'free' ? 'bg-cyan-500/20 text-cyan-400' :
                  subscription.subscription === 'storyteller' || subscription.subscription === 'pro' ? 'bg-cyan-500/20 text-cyan-400' :
                  subscription.subscription === 'author' ? 'bg-violet-500/20 text-violet-400' :
                  subscription.subscription === 'promo' ? 'bg-amber-500/20 text-amber-400' :
                  'bg-emerald-500/20 text-emerald-400'
                }`}>
                  {subscription.icon === 'crown' ? <Crown className="w-4 h-4" /> :
                   subscription.icon === 'star' ? <Star className="w-4 h-4" /> :
                   subscription.icon === 'shield' ? <Shield className="w-4 h-4" /> :
                   <Sparkles className="w-4 h-4" />}
                </div>
                <div className="min-w-0">
                  <p className={`font-semibold text-sm ${
                    subscription.subscription === 'free' ? 'text-cyan-300' :
                    subscription.subscription === 'storyteller' || subscription.subscription === 'pro' ? 'text-cyan-300' :
                    subscription.subscription === 'author' ? 'text-violet-300' :
                    subscription.subscription === 'promo' ? 'text-amber-300' :
                    'text-emerald-300'
                  }`}>
                    {subscription.plan_name || 'Freebum'} 
                    {subscription.remaining?.generations === -1 
                      ? ' • Unlimited' 
                      : ` • ${subscription.remaining?.generations ?? generationsRemaining} left`
                    }
                  </p>
                  <p className={`text-xs hidden sm:block ${
                    subscription.subscription === 'free' ? 'text-cyan-400/70' : 'text-slate-400'
                  }`}>
                    {subscription.subscription === 'free' 
                      ? 'Upgrade to Shweebum Plus ($10/mo) for AI writing' 
                      : subscription.subscription === 'storyteller' || subscription.subscription === 'pro'
                      ? 'Upgrade to Shweebum Premium ($20/mo) for unlimited'
                      : subscription.subscription === 'author'
                      ? 'Full access to all features'
                      : subscription.subscription === 'promo'
                      ? `Promo trial${subscription.promo_days_remaining != null ? ` — ${subscription.promo_days_remaining} days remaining` : ''}`
                      : 'Family & Friends - Full access'}
                  </p>
                </div>
              </div>
              {(subscription.subscription === 'free' || subscription.subscription === 'storyteller' || subscription.subscription === 'pro' || subscription.subscription === 'promo') && (
                <Link to="/pricing" className="hidden sm:block">
                  <Button size="sm" variant="outline" className={`${
                    subscription.subscription === 'free' ? 'border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/10' :
                    'border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10'
                  }`}>
                    Upgrade
                  </Button>
                </Link>
              )}
              {subscription.subscription !== 'free' && subscription.subscription !== 'family' && (
                subscription.cancel_pending ? (
                  <div className="hidden sm:flex items-center gap-2">
                    <span className="text-xs text-cyan-400">
                      Cancels {subscription.period_end ? new Date(subscription.period_end).toLocaleDateString() : 'at period end'}
                    </span>
                    <button 
                      onClick={handleReactivateSubscription}
                      className="text-xs text-cyan-400 hover:text-cyan-300 underline underline-offset-2"
                      data-testid="reactivate-subscription-btn"
                    >
                      Undo
                    </button>
                  </div>
                ) : (
                  <button 
                    onClick={() => setShowCancelDialog(true)}
                    className="hidden sm:block text-xs text-slate-400 hover:text-slate-300 underline underline-offset-2"
                    data-testid="cancel-subscription-btn"
                  >
                    Cancel Plan
                  </button>
                )
              )}
            </div>
          </div>
        </div>
      )}

      <main className="w-full px-4 py-4 sm:py-8">
        {/* Hero Welcome Section - Destiny Neon Theme */}
        <div className="relative mb-6 sm:mb-10">
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-violet-500/5 to-cyan-500/10 rounded-3xl blur-3xl" />
          <div className="relative bg-[#0F1629]/80 backdrop-blur-sm rounded-2xl border border-cyan-500/20 p-4 sm:p-8">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 sm:gap-6">
              <div>
                <p className="text-xs text-cyan-400/70 uppercase tracking-widest mb-1 font-medium">
                  {(() => {
                    const h = new Date().getHours();
                    if (h < 12) return "Good morning";
                    if (h < 17) return "Good afternoon";
                    return "Good evening";
                  })()}
                </p>
                <h1 className="font-literary text-2xl sm:text-4xl md:text-5xl text-white mb-2 text-glow-cyan" data-testid="dashboard-title">
                  Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
                </h1>
                <p className="text-slate-400 text-sm sm:text-lg">Your stories are waiting. Let's keep writing.</p>
                {/* Stats row - always rendered with min-height to prevent layout shift */}
                <div className="min-h-[32px] mt-3 sm:mt-4">
                {projects.length > 0 && (
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-400">
                      <BookOpen className="w-4 h-4 text-cyan-400" />
                      <span>{projects.length} book{projects.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-400">
                      <FileText className="w-4 h-4 text-cyan-400" />
                      <span>{projects.reduce((acc, p) => acc + (p.word_count || 0), 0).toLocaleString()} total words</span>
                    </div>
                    {/* Writing streak indicator */}
                    {(() => {
                      const streak = parseInt(localStorage.getItem('shweebum_write_streak') || '0');
                      if (streak > 1) {
                        return (
                          <div className="flex items-center gap-1 text-sm text-cyan-400 bg-cyan-500/15 px-2 py-1 rounded-full border border-cyan-500/20">
                            <span>🔥</span>
                            <span>{streak} day streak!</span>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}
                </div>
              </div>
              <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:items-center gap-2 w-full md:w-auto md:justify-end">
                {/* Selection Mode Toggle */}
                {projects.length > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSelectionMode(!selectionMode);
                      if (selectionMode) setSelectedBooks(new Set());
                    }}
                    className={`border-slate-600 w-full sm:w-auto ${selectionMode ? 'bg-red-500/20 border-red-500/50 text-red-400' : 'text-slate-300 hover:bg-slate-800'}`}
                    data-testid="toggle-selection-mode-btn"
                  >
                    {selectionMode ? (
                      <>
                        <X className="w-4 h-4 mr-2" />
                        Cancel
                      </>
                    ) : (
                      <>
                        <CheckSquare className="w-4 h-4 mr-2" />
                        Select
                      </>
                    )}
                  </Button>
                )}
                <Button 
                  variant="outline"
                  onClick={() => navigate('/vault')}
                  className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 hover:border-emerald-400 shadow-lg shadow-emerald-500/20 text-sm sm:text-base px-3 sm:px-5 py-2.5 sm:py-6 w-full sm:w-auto"
                  data-testid="vault-btn"
                >
                  <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 shrink-0" />
                  <span>Story Vault</span>
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/image-studio')}
                  className="border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10 hover:border-fuchsia-400 shadow-lg shadow-fuchsia-500/20 text-sm sm:text-base px-3 sm:px-5 py-2.5 sm:py-6 w-full sm:w-auto"
                  data-testid="image-studio-btn"
                >
                  <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 shrink-0" />
                  <span>Image Studio</span>
                </Button>
                <Button 
                  onClick={() => setShowProjectTypeChooser(true)}
                  className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 shadow-xl shadow-cyan-500/30 text-white text-base sm:text-lg px-4 sm:px-6 py-2.5 sm:py-6 glow-cyan col-span-2 sm:col-span-1 w-full sm:w-auto"
                  data-testid="new-book-btn"
                >
                  <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
                  New Book
                </Button>
              </div>
            </div>
            
            {/* Bulk Action Bar - appears when in selection mode */}
            {selectionMode && (
              <div className="mt-4 flex items-center justify-between bg-slate-800/50 rounded-lg p-3 border border-slate-700">
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={selectAllBooks}
                    className="text-slate-300 hover:text-white"
                    data-testid="select-all-btn"
                  >
                    {selectedBooks.size === projects.length ? (
                      <>
                        <CheckSquare className="w-4 h-4 mr-2 text-cyan-400" />
                        Deselect All
                      </>
                    ) : (
                      <>
                        <Square className="w-4 h-4 mr-2" />
                        Select All
                      </>
                    )}
                  </Button>
                  <span className="text-sm text-slate-400">
                    {selectedBooks.size} of {projects.length} selected
                  </span>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                  disabled={selectedBooks.size === 0}
                  className="bg-red-600 hover:bg-red-700 disabled:opacity-50"
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Selected ({selectedBooks.size})
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Word Usage Tracker - Destiny Theme */}
        {wordUsage && (
          <div className="bg-[#0F1629]/80 border border-slate-700/50 rounded-xl p-4 mb-6" data-testid="word-usage-tracker">
            {wordUsage.is_owner ? (
              /* Owner view: just a reset button */
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-cyan-400" />
                  <span className="text-sm font-medium text-slate-200">Monthly Usage</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-400 border border-cyan-500/20">Owner</span>
                </div>
                <button
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/usage/${user.id}/reset`);
                      fetchWordUsage();
                      toast.success("Monthly usage reset");
                    } catch (err) {
                      toast.error("Failed to reset usage");
                    }
                  }}
                  className="px-3 py-1.5 text-xs font-medium bg-red-500/15 text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/25 transition-colors"
                  data-testid="reset-usage-btn"
                >
                  <RotateCcw className="w-3 h-3 inline mr-1" />
                  Reset Usage
                </button>
              </div>
            ) : (
              /* Regular user view: progress bar + word count */
              <>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-cyan-400" />
                    <span className="text-sm font-medium text-slate-200">Monthly Usage</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-400 border border-cyan-500/30">{wordUsage.plan_name}</span>
                  </div>
                  <span className="text-xs text-slate-500">
                    {wordUsage.chapter_gen_parts === 3 ? "3-part chapters (longer)" : "2-part chapters"}
                  </span>
                </div>
                <div className="w-full bg-slate-800 rounded-full h-2.5 mb-1.5">
                  <div 
                    className={`h-2.5 rounded-full transition-all ${wordUsage.percent_used > 90 ? 'bg-red-500' : wordUsage.percent_used > 70 ? 'bg-cyan-500' : 'bg-cyan-500'}`}
                    style={{ width: `${Math.min(wordUsage.percent_used, 100)}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-slate-500">
                  <span>{(wordUsage.words_this_month || 0).toLocaleString()} words used</span>
                  <span>{(wordUsage.monthly_limit || 0).toLocaleString()} limit</span>
                </div>
              </>
            )}
          </div>
        )}

        {/* Promo Status Card + Mid-Trial Nudge */}
        {subscription?.subscription === 'promo' && (
          <div className="mb-6 bg-gradient-to-r from-amber-950/30 to-slate-900/50 border border-amber-500/25 rounded-xl p-4" data-testid="promo-status-card">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-semibold text-amber-300">Shweebum Promo Active</span>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="bg-slate-800/50 rounded-lg p-2.5 text-center">
                <p className="text-lg font-bold text-white">{subscription.promo_days_remaining ?? '—'}</p>
                <p className="text-xs text-slate-400">days left</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-2.5 text-center">
                <p className="text-lg font-bold text-white">{subscription.generation_usage?.chapters_this_month ?? 0} / 10</p>
                <p className="text-xs text-slate-400">chapters</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-2.5 text-center">
                <p className="text-lg font-bold text-white">{subscription.generation_usage?.books_this_month ?? 0} / 1</p>
                <p className="text-xs text-slate-400">books</p>
              </div>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed">
              You're currently writing with our Premium AI engine — experience faster, richer storytelling.
            </p>
            {subscription.promo_days_remaining != null && subscription.promo_days_remaining <= 3 && !subscription.promo_expired && (
              <div className="mt-3 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 flex items-center justify-between" data-testid="promo-midtrial-nudge">
                <p className="text-xs text-amber-300">
                  You're halfway through your Promo. Upgrade anytime to keep writing without limits.
                </p>
                <Link to="/pricing">
                  <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-black text-xs ml-3 shrink-0">
                    Upgrade
                  </Button>
                </Link>
              </div>
            )}
            {subscription.promo_expired && (
              <div className="mt-3 bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="promo-expired-banner">
                <p className="text-sm font-medium text-red-300 mb-2">
                  Your Shweebum Promo has ended.
                </p>
                <p className="text-xs text-red-400/80 mb-3">
                  Upgrade to continue writing with high-quality AI storytelling.
                </p>
                <Link to="/pricing">
                  <Button size="sm" className="bg-red-500 hover:bg-red-600 text-white text-xs">
                    View Plans
                  </Button>
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Free Planning Studio Promo Banner - Destiny Neon Theme */}
        <div className="mb-6 bg-gradient-to-r from-cyan-950/40 via-slate-900/60 to-cyan-950/40 border border-cyan-500/20 rounded-2xl p-5 relative overflow-hidden" data-testid="free-studio-promo">
          <div className="absolute top-0 right-0 w-40 h-40 bg-cyan-500/10 rounded-full blur-3xl" />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-cyan-500/15 rounded-xl flex items-center justify-center shrink-0 border border-cyan-500/30">
                <Sparkles className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-cyan-300 text-base">Story Planning Studio</h3>
                  <span className="text-[10px] font-bold bg-emerald-500/15 text-emerald-400 px-2 py-0.5 rounded-full border border-emerald-500/30">FREE FOREVER</span>
                </div>
                <p className="text-slate-400 text-sm max-w-lg">
                  Plan unlimited books for free — chapter outlines, character creation, story doctor analysis. 
                  Share with friends who want to become authors!
                </p>
                <div className="flex flex-wrap items-center gap-3 mt-2">
                  <span className="text-xs text-cyan-400/80 flex items-center gap-1"><Check className="w-3 h-3" /> Chapter Planning</span>
                  <span className="text-xs text-cyan-400/80 flex items-center gap-1"><Check className="w-3 h-3" /> Character Creator</span>
                  <span className="text-xs text-cyan-400/80 flex items-center gap-1"><Check className="w-3 h-3" /> Story Doctor</span>
                  <span className="text-xs text-cyan-400/80 flex items-center gap-1"><Check className="w-3 h-3" /> No Credit Card</span>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 shrink-0">
              <Link to="/scratchpad">
                <Button
                  variant="outline"
                  className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300"
                  data-testid="free-scratchpad-btn"
                >
                  <PenTool className="w-4 h-4 mr-2" />
                  Free Writing Pad
                </Button>
              </Link>
              <Button
                onClick={() => setShowShareRewards(true)}
                className="bg-gradient-to-r from-cyan-500/80 to-violet-500/80 hover:from-cyan-500 hover:to-violet-500 text-white border-0"
                data-testid="share-rewards-btn"
              >
                <Gift className="w-4 h-4 mr-2" />
                Share & Get FREE Gifts!
              </Button>
            </div>
          </div>
        </div>

        {/* Stories in Progress (Drafts) Section - Destiny Theme */}
        {(() => {
          const allDrafts = [];
          try {
            // Get drafts from user-specific localStorage keys
            const uid = user?.id || '';
            const userStoryKey = `shweebum_story_drafts_${uid}`;
            const userJourneyKey = `shweebum_journey_drafts_${uid}`;
            
            // Only show drafts from user-specific keys (NOT generic ones that may belong to another user)
            const storyDrafts = uid ? JSON.parse(localStorage.getItem(userStoryKey) || '{}') : {};
            const journeyDrafts = uid ? JSON.parse(localStorage.getItem(userJourneyKey) || '{}') : {};
            
            Object.values(storyDrafts).forEach(d => allDrafts.push(d));
            Object.values(journeyDrafts).forEach(d => {
              if (!allDrafts.find(existing => existing.title === d.title)) {
                allDrafts.push({ ...d, mode: 'journey' });
              }
            });
          } catch (e) {}
          
          if (allDrafts.length === 0) return null;
          
          return (
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-violet-500/15 flex items-center justify-center border border-violet-500/30">
                  <Clock className="w-4 h-4 text-violet-400" />
                </div>
                <h2 className="font-semibold text-lg text-white">Stories in Progress</h2>
                <Badge variant="outline" className="text-violet-400 border-violet-500/40">
                  {allDrafts.length} draft{allDrafts.length !== 1 ? 's' : ''}
                </Badge>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {allDrafts
                  .sort((a, b) => new Date(b.savedAt) - new Date(a.savedAt))
                  .slice(0, 4)
                  .map((draft, idx) => (
                  <Card 
                    key={idx}
                    className="bg-violet-950/30 border-violet-500/20 hover:border-violet-400/40 hover:shadow-lg hover:shadow-violet-500/10 transition-all cursor-pointer"
                    onClick={() => {
                      // Load the draft and open the dialog
                      if (draft.mode === 'journey') {
                        setShowNewProject(true);
                        // We'll need to set up the state after dialog opens
                        setTimeout(() => {
                          const event = new CustomEvent('load-draft', { detail: draft });
                          window.dispatchEvent(event);
                        }, 100);
                      } else {
                        setShowNewProject(true);
                        setTimeout(() => {
                          const event = new CustomEvent('load-draft', { detail: draft });
                          window.dispatchEvent(event);
                        }, 100);
                      }
                    }}
                    data-testid={`draft-card-${idx}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-white truncate">{draft.title || "Untitled"}</h3>
                          <p className="text-xs text-slate-400 mt-1">
                            {draft.mode === 'journey' ? 'Journey Mode' : draft.mode === 'idea' ? 'Idea Mode' : 'Manual'}
                            {draft.blueprintData?.genre && ` · ${draft.blueprintData.genre}`}
                            {draft.formData?.genre && ` · ${draft.formData.genre}`}
                          </p>
                          <p className="text-xs text-violet-400 mt-2">
                            Saved {new Date(draft.savedAt).toLocaleDateString()}
                            {draft.isAutoSave && <span className="text-slate-500 ml-1">(auto)</span>}
                          </p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10 ml-2 shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Delete draft
                            try {
                              const uid = user?.id || '';
                              const storyKey = localStorage.getItem(`shweebum_story_drafts_${uid}`) ? `shweebum_story_drafts_${uid}` : 'shweebum_story_drafts';
                              const journeyKey = localStorage.getItem(`shweebum_journey_drafts_${uid}`) ? `shweebum_journey_drafts_${uid}` : 'shweebum_journey_drafts';
                              const storyDrafts = JSON.parse(localStorage.getItem(storyKey) || '{}');
                              const journeyDrafts = JSON.parse(localStorage.getItem(journeyKey) || '{}');
                              
                              // Remove from both storage locations
                              Object.keys(storyDrafts).forEach(key => {
                                if (storyDrafts[key].title === draft.title) delete storyDrafts[key];
                              });
                              Object.keys(journeyDrafts).forEach(key => {
                                if (journeyDrafts[key].title === draft.title) delete journeyDrafts[key];
                              });
                              
                              localStorage.setItem(storyKey, JSON.stringify(storyDrafts));
                              localStorage.setItem(journeyKey, JSON.stringify(journeyDrafts));
                              
                              toast.success(`Deleted draft: "${draft.title}"`);
                              // Force re-render
                              window.location.reload();
                            } catch (err) {
                              toast.error("Failed to delete draft");
                            }
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Daily Inspiration Corner - Destiny Theme */}
        {projects.length > 0 && (
          <div className="mb-6 sm:mb-8 p-3 sm:p-4 bg-gradient-to-r from-violet-950/40 via-purple-950/30 to-violet-950/40 rounded-xl border border-violet-500/20">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="w-10 h-10 rounded-full bg-violet-500/15 flex items-center justify-center shrink-0 border border-violet-500/30">
                <Lightbulb className="w-5 h-5 text-violet-400" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-violet-300 text-sm mb-1">Daily Writing Spark ✨</h3>
                {(() => {
                  const tips = [
                    { tip: "Your unique voice is your superpower. No AI can replicate the story only YOU can tell.", action: "What makes your perspective special?" },
                    { tip: "Writer's block? Start with 'What if...' and let your imagination wander.", action: "Try: What if your character's biggest fear came true?" },
                    { tip: "Great stories come from truth wrapped in fiction. Draw from real emotions.", action: "What feeling do you want readers to experience?" },
                    { tip: "Don't edit while you create. Let the words flow first, polish later.", action: "Today: Write 500 words without looking back." },
                    { tip: "Every chapter should leave readers wanting more. End on curiosity.", action: "What question will keep them turning pages?" },
                    { tip: "Characters become real when they want something badly and face obstacles.", action: "What does your protagonist fear losing?" },
                    { tip: "The best villains believe they're the hero of their own story.", action: "What justifies your antagonist's actions in their mind?" },
                  ];
                  const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
                  const todayTip = tips[dayOfYear % tips.length];
                  return (
                    <>
                      <p className="text-sm text-violet-200">{todayTip.tip}</p>
                      <p className="text-xs text-violet-400/80 mt-1 italic">{todayTip.action}</p>
                    </>
                  );
                })()}
              </div>
              <p className="text-xs text-violet-500 shrink-0 hidden sm:block">You're the author.<br/>I'm just here to help.</p>
            </div>
          </div>
        )}

        {/* Projects Grid - Destiny Theme */}
        <div className="min-h-[300px]">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <Card key={i} className="bg-[#0F1629]/80 backdrop-blur-sm border-slate-700/50">
                <CardHeader className="animate-pulse">
                  <div className="h-5 w-20 bg-slate-700 rounded mb-3" />
                  <div className="h-6 w-3/4 bg-slate-700 rounded" />
                </CardHeader>
                <CardContent className="animate-pulse">
                  <div className="flex gap-4 mb-4">
                    <div className="h-4 w-20 bg-slate-700 rounded" />
                    <div className="h-4 w-24 bg-slate-700 rounded" />
                  </div>
                  <div className="h-10 w-full bg-slate-700 rounded" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-16 bg-[#0F1629]/60 backdrop-blur-sm rounded-2xl border border-cyan-500/20">
            <div className="relative inline-block">
              <div className="absolute inset-0 bg-cyan-500/20 rounded-full blur-2xl" />
              <ShweebumCat size="lg" />
            </div>
            <h3 className="font-literary text-2xl text-white mt-6">No books yet</h3>
            <p className="text-slate-400 mt-2 max-w-md mx-auto">
              Start your first book and Shweebum will help you write it! 
              Choose from three powerful creation modes.
            </p>
            <Button 
              onClick={() => setShowNewProject(true)}
              className="mt-6 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 shadow-xl shadow-cyan-500/30 glow-cyan"
            >
              <Feather className="w-4 h-4 mr-2" />
              Start Writing
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 w-full" data-testid="projects-grid">
            {projects.map(project => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full"
              >
                <Card 
                  className={`bg-[#0F1629]/80 backdrop-blur-sm hover:shadow-xl hover:shadow-cyan-500/10 transition-all duration-300 cursor-pointer group border-slate-700/50 hover:border-cyan-500/40 w-full ${selectionMode && selectedBooks.has(project.id) ? 'ring-2 ring-cyan-500 border-cyan-500' : ''}`} 
                  data-testid={`project-card-${project.id}`}
                  onClick={() => {
                    if (selectionMode) {
                      toggleBookSelection(project.id, { stopPropagation: () => {} });
                    } else if (project.project_type === "cookbook_studio") {
                      navigate(`/cookbook/${project.id}`);
                    } else if (project.project_type === "life_story_studio") {
                      navigate(`/life-story/${project.id}`);
                    } else {
                      navigate(`/write/${project.id}`);
                    }
                  }}
                >
                  <CardHeader className="p-4 sm:p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        {/* Selection Checkbox */}
                        {selectionMode && (
                          <button
                            onClick={(e) => toggleBookSelection(project.id, e)}
                            className={`mt-1 w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                              selectedBooks.has(project.id) 
                                ? 'bg-cyan-500 border-cyan-500 text-white' 
                                : 'border-slate-500 hover:border-cyan-400'
                            }`}
                            data-testid={`select-book-${project.id}`}
                          >
                            {selectedBooks.has(project.id) && <Check className="w-4 h-4" />}
                          </button>
                        )}
                        <div>
                          <Badge variant="secondary" className={`mb-2 capitalize ${project.target_length === 'quick_story' ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-slate-700/50 text-slate-300'}`}>
                            {project.target_length === 'quick_story' ? 'Short Story' : project.genre}
                          </Badge>
                          <CardTitle className="font-literary text-white">{project.title}</CardTitle>
                        </div>
                      </div>
                      {!selectionMode && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (confirm(`Delete "${project.title}"? This cannot be undone.`)) {
                              handleDeleteProject(project.id);
                            }
                          }}
                          className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity text-slate-500 hover:text-red-400 p-2"
                          data-testid={`delete-project-${project.id}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Progress bar */}
                    {(() => {
                      const target = project.target_length === 'quick_story' ? 8000 : project.target_length === 'novella' ? 40000 : 80000;
                      const pct = Math.min(100, Math.round(((project.word_count || 0) / target) * 100));
                      return (
                        <div className="mb-3">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-[11px] text-slate-500">Progress</span>
                            <span className="text-[11px] text-cyan-400 font-medium">{pct}%</span>
                          </div>
                          <div className="w-full bg-slate-800/60 rounded-full h-1.5 overflow-hidden">
                            <div 
                              className="h-full rounded-full transition-all duration-500"
                              style={{ 
                                width: `${pct}%`,
                                background: pct >= 75 ? 'linear-gradient(90deg, #34D399, #22D3EE)' : pct >= 40 ? 'linear-gradient(90deg, #22D3EE, #A78BFA)' : 'linear-gradient(90deg, #A78BFA, #22D3EE)'
                              }}
                            />
                          </div>
                        </div>
                      );
                    })()}
                    <div className="flex items-center gap-4 text-sm text-slate-400">
                      <span className="flex items-center gap-1">
                        <FileText className="w-4 h-4 text-cyan-400" />
                        {(project.word_count || 0).toLocaleString()} words
                      </span>
                      <span className="flex items-center gap-1 text-xs">
                        <Clock className="w-3 h-3 text-slate-500" />
                        {(() => {
                          const updated = new Date(project.updated_at);
                          const now = new Date();
                          const diffMs = now - updated;
                          const diffMins = Math.floor(diffMs / 60000);
                          const diffHours = Math.floor(diffMs / 3600000);
                          const diffDays = Math.floor(diffMs / 86400000);
                          if (diffMins < 60) return diffMins <= 1 ? 'Just now' : `${diffMins}m ago`;
                          if (diffHours < 24) return `${diffHours}h ago`;
                          if (diffDays < 7) return `${diffDays}d ago`;
                          return updated.toLocaleDateString();
                        })()}
                      </span>
                    </div>
                    <Button 
                      variant="gold"
                      className="w-full mt-4" 
                      data-testid={`open-project-${project.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/write/${project.id}`);
                      }}
                    >
                      Continue Writing
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
        </div>
      </main>

      {/* New Project Dialog */}
      <NewProjectDialog 
        open={showNewProject} 
        onOpenChange={setShowNewProject}
        genres={genres}
        userId={user?.id}
        onCreated={(project) => {
          setProjects([project, ...projects]);
          setShowNewProject(false);
          navigate(`/write/${project.id}`);
        }}
      />

      {/* Project Type Chooser — unified entry point for Story Studio / Cookbook Studio / Life Story */}
      <Dialog open={showProjectTypeChooser} onOpenChange={setShowProjectTypeChooser}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 sm:max-w-2xl" data-testid="project-type-chooser">
          <DialogHeader>
            <DialogTitle>What are you creating?</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Pick the kind of book you want to build — Story Studio for fiction, Cookbook Studio for recipes, or Life Story Studio for memoirs and biographies.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
            <button
              onClick={() => { setShowProjectTypeChooser(false); setShowNewProject(true); }}
              className="text-left p-4 rounded-lg border border-cyan-500/30 hover:border-cyan-400 hover:bg-cyan-500/5 transition-colors"
              data-testid="ptype-story-studio"
            >
              <Feather className="w-6 h-6 text-cyan-400 mb-2" />
              <h3 className="font-semibold text-cyan-200">Story Studio</h3>
              <p className="text-xs text-slate-400 mt-1">Novels, novellas, series. AI-assisted fiction.</p>
            </button>
            <button
              onClick={() => { setShowProjectTypeChooser(false); setShowNewCookbook(true); }}
              className="text-left p-4 rounded-lg border border-amber-500/30 hover:border-amber-400 hover:bg-amber-500/5 transition-colors"
              data-testid="ptype-cookbook-studio"
            >
              <BookOpen className="w-6 h-6 text-amber-400 mb-2" />
              <h3 className="font-semibold text-amber-200">Cookbook Studio</h3>
              <p className="text-xs text-slate-400 mt-1">Recipes, parts &amp; sections, family contributors.</p>
            </button>
            <button
              onClick={() => { setShowProjectTypeChooser(false); setShowNewLifeStory(true); }}
              className="text-left p-4 rounded-lg border border-purple-500/30 hover:border-purple-400 hover:bg-purple-500/5 transition-colors"
              data-testid="ptype-life-story-studio"
            >
              <Heart className="w-6 h-6 text-purple-400 mb-2" />
              <h3 className="font-semibold text-purple-200">Life Story Studio</h3>
              <p className="text-xs text-slate-400 mt-1">Memoirs, biographies, family histories &amp; legacy.</p>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Cookbook quick-create */}
      <Dialog open={showNewCookbook} onOpenChange={setShowNewCookbook}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100" data-testid="new-cookbook-dialog">
          <DialogHeader>
            <DialogTitle>New Cookbook</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Choose your cookbook style. You can add parts, sections, and recipes after creation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-400">Title</label>
              <Input
                value={newCookbookData.title}
                onChange={(e) => setNewCookbookData({ ...newCookbookData, title: e.target.value })}
                placeholder="Family Favorites"
                data-testid="new-cookbook-title"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400">Style</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: "single_author", label: "Single author" },
                  { id: "family", label: "Family" },
                  { id: "collaborative", label: "Collaborative" },
                ].map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setNewCookbookData({ ...newCookbookData, style: s.id })}
                    className={`p-2 rounded text-xs border ${newCookbookData.style === s.id ? "border-amber-400 bg-amber-500/10 text-amber-200" : "border-slate-700 text-slate-400"}`}
                    data-testid={`new-cookbook-style-${s.id}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400">Cuisine (optional)</label>
              <Input
                value={newCookbookData.cuisine}
                onChange={(e) => setNewCookbookData({ ...newCookbookData, cuisine: e.target.value })}
                placeholder="Italian, Japanese, mixed…"
                data-testid="new-cookbook-cuisine"
              />
            </div>
            <Button
              onClick={async () => {
                if (!newCookbookData.title.trim()) {
                  toast.error("Title is required");
                  return;
                }
                setCreatingCookbook(true);
                try {
                  const token = localStorage.getItem("shweebum_token");
                  const r = await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/api/cookbooks`,
                    newCookbookData,
                    { headers: { Authorization: `Bearer ${token}` } },
                  );
                  setShowNewCookbook(false);
                  setNewCookbookData({ title: "", style: "single_author", cuisine: "" });
                  navigate(`/cookbook/${r.data.id}`);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || e.message);
                } finally {
                  setCreatingCookbook(false);
                }
              }}
              disabled={creatingCookbook || !newCookbookData.title.trim()}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-600"
              data-testid="new-cookbook-submit"
            >
              {creatingCookbook ? "Creating…" : "Create cookbook"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Life Story quick-create */}
      <Dialog open={showNewLifeStory} onOpenChange={setShowNewLifeStory}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100" data-testid="new-life-story-dialog">
          <DialogHeader>
            <DialogTitle>New Life Story</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Pick the mode that fits — memoir, biography, autobiography, family history, or legacy project.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-400">Title</label>
              <Input
                value={newLifeStoryData.title}
                onChange={(e) => setNewLifeStoryData({ ...newLifeStoryData, title: e.target.value })}
                placeholder="My Father's Story"
                data-testid="new-life-story-title"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400">Mode</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {[
                  { id: "memoir", label: "Memoir" },
                  { id: "autobiography", label: "Autobio" },
                  { id: "biography", label: "Biography" },
                  { id: "family_history", label: "Family History" },
                  { id: "legacy_project", label: "Legacy" },
                ].map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setNewLifeStoryData({ ...newLifeStoryData, mode: m.id })}
                    className={`p-2 rounded text-xs border ${newLifeStoryData.mode === m.id ? "border-purple-400 bg-purple-500/10 text-purple-200" : "border-slate-700 text-slate-400"}`}
                    data-testid={`new-life-story-mode-${m.id}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400">Subject name (optional)</label>
              <Input
                value={newLifeStoryData.subject_name}
                onChange={(e) => setNewLifeStoryData({ ...newLifeStoryData, subject_name: e.target.value })}
                placeholder="Whose life story is this? Defaults to you."
                data-testid="new-life-story-subject"
              />
            </div>
            <Button
              onClick={async () => {
                if (!newLifeStoryData.title.trim()) {
                  toast.error("Title is required");
                  return;
                }
                setCreatingLifeStory(true);
                try {
                  const token = localStorage.getItem("shweebum_token");
                  const r = await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/api/life-stories`,
                    newLifeStoryData,
                    { headers: { Authorization: `Bearer ${token}` } },
                  );
                  setShowNewLifeStory(false);
                  setNewLifeStoryData({ title: "", mode: "memoir", subject_name: "" });
                  navigate(`/life-story/${r.data.id}`);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || e.message);
                } finally {
                  setCreatingLifeStory(false);
                }
              }}
              disabled={creatingLifeStory || !newLifeStoryData.title.trim()}
              className="w-full bg-gradient-to-r from-purple-500 to-fuchsia-600"
              data-testid="new-life-story-submit"
            >
              {creatingLifeStory ? "Creating…" : "Create life story"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Welcome Popup for New Users */}
      <WelcomePopup 
        open={showWelcome} 
        onClose={handleWelcomeClose} 
        onSkip={() => {
          // Skip both welcome popup and tour
          setShowWelcome(false);
          setShowTour(false);
          // Mark as welcomed and toured so they don't see these again
          if (user?.id) {
            localStorage.setItem(`shweebum_welcomed_${user.id}`, 'true');
            localStorage.setItem(`shweebum_toured_${user.id}`, 'true');
          }
        }}
        userName={user?.name?.split(' ')[0]} 
      />
      
      {/* Guided Tour */}
      <GuidedTour 
        active={showTour} 
        onComplete={handleTourComplete}
      />

      {/* My Style Profile Dialog - Destiny Theme */}
      <Dialog open={showStyleDialog} onOpenChange={setShowStyleDialog}>
        <DialogContent className="max-w-lg bg-[#0F1629] border-violet-500/30">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Fingerprint className="w-5 h-5 text-violet-400" />
              Continue My Style
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Upload a sample of your writing and I'll learn to match your unique voice.
            </DialogDescription>
          </DialogHeader>

          {styleProfile ? (
            <div className="space-y-4">
              <div className="bg-violet-950/40 p-4 rounded-lg border border-violet-500/30">
                <div className="flex items-center gap-2 mb-3">
                  <Check className="w-5 h-5 text-violet-400" />
                  <span className="font-semibold text-violet-300">Style Profile Active</span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-slate-400">Sentence Style:</span>
                    <p className="capitalize text-white">{styleProfile.sentence_style?.avg_length} / {styleProfile.sentence_style?.complexity}</p>
                  </div>
                  <div>
                    <span className="text-slate-400">Vocabulary:</span>
                    <p className="capitalize text-white">{styleProfile.vocabulary?.level}</p>
                  </div>
                  <div>
                    <span className="text-slate-400">Narrative Voice:</span>
                    <p className="capitalize text-white">{styleProfile.narrative_voice?.formality}</p>
                  </div>
                  <div>
                    <span className="text-slate-400">Pacing:</span>
                    <p className="capitalize text-white">{styleProfile.pacing?.action_to_reflection}</p>
                  </div>
                </div>
                {styleProfile.signature_elements && (
                  <p className="text-xs text-slate-400 mt-3 italic">
                    "{styleProfile.signature_elements}"
                  </p>
                )}
              </div>

              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700/50"
                  onClick={async () => {
                    try {
                      await axios.delete(`${API}/style-profile/${user.id}`);
                      setStyleProfile(null);
                      toast.success("Style profile cleared");
                    } catch (e) {
                      toast.error("Failed to clear profile");
                    }
                  }}
                >
                  Clear Profile
                </Button>
                <Button 
                  className="flex-1 bg-violet-600 hover:bg-violet-500 text-white"
                  onClick={() => setShowStyleDialog(false)}
                >
                  Done
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-slate-200">
                  Paste a Sample of Your Writing
                </label>
                <Textarea
                  value={styleSample}
                  onChange={(e) => setStyleSample(e.target.value)}
                  placeholder="Paste at least 500 words of your writing here. The more you provide (2,000-5,000 words is ideal), the better I can capture your unique voice..."
                  className="min-h-[200px] bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-500"
                  data-testid="style-sample-input"
                />
                <p className="text-xs text-slate-400 mt-1">
                  {styleSample.split(' ').filter(w => w).length} words · Minimum 500 words recommended
                </p>
              </div>

              <div className="bg-slate-800/50 p-3 rounded-lg border border-slate-700">
                <p className="text-sm text-slate-300">
                  <Brain className="w-4 h-4 inline mr-1 text-violet-400" />
                  I'll analyze your sentence structure, vocabulary, dialogue style, pacing, and narrative voice to create stories that sound like <em className="text-violet-300">you</em>.
                </p>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowStyleDialog(false)} className="border-slate-600 text-slate-300 hover:bg-slate-700/50">Cancel</Button>
                <Button 
                  className="bg-violet-600 hover:bg-violet-500 text-white btn-pop glow-void"
                  disabled={styleSample.split(' ').filter(w => w).length < 100 || analyzingStyle}
                  onClick={async () => {
                    setAnalyzingStyle(true);
                    try {
                      const res = await axios.post(`${API}/style-profile/analyze`, {
                        sample_text: styleSample,
                        user_id: user.id
                      });
                      setStyleProfile(res.data.style_profile);
                      toast.success("Your writing style has been captured!");
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Analysis failed");
                    } finally {
                      setAnalyzingStyle(false);
                    }
                  }}
                  data-testid="analyze-style-btn"
                >
                  {analyzingStyle ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Fingerprint className="w-4 h-4 mr-2" />
                      Learn My Style
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Share Rewards Modal */}
      <ShareRewardsModal
        open={showShareRewards}
        onClose={() => setShowShareRewards(false)}
        user={user}
        projects={projects}
        onRewardsClaimed={() => {
          // Refresh subscription data to pick up the extra chapters
          if (user?.id) {
            axios.get(`${API}/user/${user.id}/subscription`).then(res => {
              setSubscription(res.data);
            }).catch(() => {});
          }
        }}
      />

      {/* Contact Support Modal */}
      <ContactSupportModal
        open={showContactSupport}
        onOpenChange={setShowContactSupport}
        user={user}
        project={null}
        API={API}
      />

      {/* Cancel Subscription Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="sm:max-w-md bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Cancel Subscription?</DialogTitle>
            <DialogDescription className="text-slate-400">
              Your <span className="text-cyan-400 font-medium">{subscription?.plan_name || 'current plan'}</span> will remain active until the end of your billing period. You won't be charged again.
            </DialogDescription>
          </DialogHeader>
          <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50 text-sm text-slate-400 space-y-2">
            <p>After your plan expires, you'll still have:</p>
            <ul className="list-disc pl-4 space-y-1">
              <li>2 book projects with 20 chapters each</li>
              <li>10 AI chapter generations</li>
              <li>Unlimited free writing</li>
              <li>All planning & outlining tools</li>
              <li>Export to TXT & EPUB</li>
            </ul>
            <p className="text-xs text-slate-400 mt-3">Your existing projects and content will NOT be deleted. You can resubscribe anytime.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCancelDialog(false)} className="border-slate-700">
              Keep My Plan
            </Button>
            <Button 
              onClick={handleCancelSubscription} 
              disabled={cancelling}
              variant="destructive"
              data-testid="confirm-cancel-btn"
            >
              {cancelling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Yes, Cancel at End of Period
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


export default Dashboard;
